import React, { useState, useRef } from 'react';
import SEO from '../components/SEO';
import { useForm, Controller } from 'react-hook-form';
import { Button } from '../components/ui/button';
import { MessageSquare, Phone, Mail, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import ReCAPTCHA from "react-google-recaptcha";
import { useCountries } from '../hooks/useCountries';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const Feedback = () => {
    const { toast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [captchaToken, setCaptchaToken] = useState(null);
    const recaptchaRef = useRef(null);
    const { countries } = useCountries();

    // Schema
    const schema = yup.object().shape({
        title: yup.string().required("Title is required"),
        firstName: yup.string().required("First Name is required"),
        lastName: yup.string().required("Last Name is required"),
        email: yup.string().email("Invalid email").required("Email is required"),
        phone: yup.string().required("Phone Number is required"),
        nationality: yup.string().required("Country is required"),
        comment: yup.string().required("Message is required"),
    });

    const { register, handleSubmit, control, formState: { errors }, reset, setError, watch, setValue } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            title: "Mr",
            nationality: "",
            countryCode: "+968"
        }
    });

    const selectedNationality = watch("nationality");

    React.useEffect(() => {
        if (selectedNationality) {
            const country = countries.find(c => String(c.id) === String(selectedNationality));
            setValue("countryCode", (country && country.phone_prefix) || "+968");
        } else if (countries.length > 0) {
            const oman = countries.find(c => c.name.toLowerCase() === 'oman');
            if (oman) {
                setValue("nationality", String(oman.id));
                setValue("countryCode", oman.phone_prefix || "+968");
            }
        }
    }, [selectedNationality, setValue, countries]);

    const handleCaptchaChange = (token) => {
        setCaptchaToken(token);
    };

    const onSubmit = async (data) => {
        const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
        if (recaptchaSiteKey && !captchaToken) {
            toast({
                title: "Validation Error",
                description: "Please complete the ReCAPTCHA verification.",
                variant: "destructive",
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('title', data.title);
            formData.append('firstName', data.firstName);
            formData.append('lastName', data.lastName);
            formData.append('phone', `${data.countryCode}${data.phone}`);
            formData.append('email', data.email);
            formData.append('nationality', data.nationality);
            formData.append('comment', data.comment);
            formData.append('recaptcha_token', captchaToken);

            const response = await axios.post('https://admin.baicoman.com/api/feedback-leads', formData);

            if (response.status === 200 || response.status === 201) {
                toast({
                    title: "Success",
                    description: "Your feedback has been sent successfully.",
                    className: "bg-green-600 text-white border-none"
                });
                reset();
                if (recaptchaSiteKey && recaptchaRef.current) {
                    recaptchaRef.current.reset();
                }
                setCaptchaToken(null);
            }
        } catch (error) {
            console.error(error);
            if (error.response && error.response.data && error.response.data.errors) {
                const serverErrors = error.response.data.errors;
                Object.keys(serverErrors).forEach((field) => {
                    setError(field, {
                        type: "server",
                        message: serverErrors[field][0],
                    });
                });
                toast({
                    title: "Validation Error",
                    description: "Please check the form for errors.",
                    variant: "destructive",
                });
            } else {
                toast({
                    title: "Error",
                    description: "Something went wrong. Please try again.",
                    variant: "destructive",
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 font-sans selection:bg-red-100 pb-20 pt-20 md:pt-[120px]">
            <SEO
                title="Feedback | BAIC Oman - Share Your Experience"
                description="Share your feedback with BAIC Oman. We value your opinion to improve our services and vehicles."
                canonicalUrl="https://baicoman.com/feedback"
            />
            <h1 className="sr-only">BAIC Oman Customer Feedback</h1>
            {/* Hero Section */}
            <div className="relative h-[50vh] w-full overflow-hidden bg-[#1a1a1a]">
                <img
                    src="/assets/images/service/feedback.BGyvPy5t.jpg"
                    alt="Service Feedback"
                    className="w-full h-full object-cover opacity-60"
                    onError={(e) => { e.target.src = 'https://images.pexels.com/photos/65623/vehicle-chrome-technology-automobile-65623.jpeg' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                <div className="absolute bottom-12 left-0 right-0">
                    <div className="max-w-7xl mx-auto px-6 lg:px-8">
                        <span className="text-red-500 font-bold tracking-[0.2em] uppercase text-sm mb-4 block animate-in fade-in slide-in-from-bottom-2 duration-700">Customer Care</span>
                        <h2 className="text-white text-5xl md:text-7xl font-bold tracking-tighter mb-4 animate-in fade-in slide-in-from-bottom-4 duration-1000">FEEDBACK</h2>
                        <p className="text-gray-300 text-lg max-w-2xl font-light leading-relaxed animate-in fade-in slide-in-from-bottom-6 duration-1000 delay-200">
                            We value your voice. Share your experience with us to help us serve you better.
                        </p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-20 relative z-20">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

                    {/* Left: Contact Info */}
                    <div className="lg:col-span-4 space-y-6">
                        <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                            <h3 className="text-xl font-bold text-gray-900 mb-6">Get in touch</h3>
                            <p className="text-gray-500 mb-8 leading-relaxed">
                                Have a specific inquiry? You can also reach our customer support team directly.
                            </p>

                            <div className="space-y-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center text-red-600">
                                        <Phone className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Phone</p>
                                        <p className="text-gray-900 font-semibold text-lg">+968 80050800</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center text-red-600">
                                        <Mail className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Email</p>
                                        <p className="text-gray-900 font-semibold text-lg">info@baicoman.com</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right: Form */}
                    <div className="lg:col-span-8">
                        <div className="bg-white rounded-[2.5rem] p-8 lg:p-12 shadow-2xl border border-gray-100">
                            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div className="space-y-2">
                                        <label className="text-sm font-bold text-gray-700 ml-1">Title</label>
                                        <Controller
                                            control={control}
                                            name="title"
                                            render={({ field }) => (
                                                <Select onValueChange={field.onChange} value={field.value}>
                                                    <SelectTrigger className="w-full bg-gray-50 border-none h-[52px] rounded-2xl focus:ring-0">
                                                        <SelectValue placeholder="Select" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="Mr">Mr.</SelectItem>
                                                        <SelectItem value="Ms">Ms.</SelectItem>
                                                        <SelectItem value="Mrs">Mrs.</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            )}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-bold text-gray-700 ml-1">Country</label>
                                        <Controller
                                            control={control}
                                            name="nationality"
                                            render={({ field }) => (
                                                <Select onValueChange={field.onChange} value={field.value}>
                                                    <SelectTrigger className="w-full bg-gray-50 border-none h-[52px] rounded-2xl focus:ring-0">
                                                        <SelectValue placeholder="Select Country" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {countries.map((country, index) => (
                                                            <SelectItem key={index} value={String(country.id)}>{country.name}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            )}
                                        />
                                        {errors.nationality && <p className="text-red-500 text-xs ml-1">{errors.nationality.message}</p>}
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-bold text-gray-700 ml-1">First Name</label>
                                        <input {...register("firstName")} type="text" placeholder="John" className="w-full p-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-red-500/20 text-gray-900 font-medium transition-all hover:bg-gray-100 placeholder-gray-400" />
                                        {errors.firstName && <p className="text-red-500 text-xs ml-1">{errors.firstName.message}</p>}
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-bold text-gray-700 ml-1">Last Name</label>
                                        <input {...register("lastName")} type="text" placeholder="Doe" className="w-full p-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-red-500/20 text-gray-900 font-medium transition-all hover:bg-gray-100 placeholder-gray-400" />
                                        {errors.lastName && <p className="text-red-500 text-xs ml-1">{errors.lastName.message}</p>}
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-bold text-gray-700 ml-1">Email</label>
                                        <input {...register("email")} type="email" placeholder="john@example.com" className="w-full p-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-red-500/20 text-gray-900 font-medium transition-all hover:bg-gray-100 placeholder-gray-400" />
                                        {errors.email && <p className="text-red-500 text-xs ml-1">{errors.email.message}</p>}
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-bold text-gray-700 ml-1">Phone Number</label>
                                        <div className="flex">
                                            <span className="p-4 bg-gray-100 border-none rounded-l-2xl text-gray-500 font-medium border-r border-gray-200 flex items-center justify-center min-w-[80px]" dir="ltr">
                                                {watch("countryCode") || "+968"}
                                            </span>
                                            <input {...register("phone")} type="tel" placeholder="50 123 4567" className="w-full p-4 bg-gray-50 border-none rounded-r-2xl focus:ring-2 focus:ring-red-500/20 text-gray-900 font-medium transition-all hover:bg-gray-100 placeholder-gray-400" />
                                        </div>
                                        {errors.phone && <p className="text-red-500 text-xs ml-1">{errors.phone.message}</p>}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-bold text-gray-700 ml-1">Your Message</label>
                                    <textarea {...register("comment")} rows={6} placeholder="Tell us more about your experience..." className="w-full p-6 bg-gray-50 border-none rounded-3xl focus:ring-2 focus:ring-red-500/20 text-gray-900 font-medium transition-all hover:bg-gray-100 placeholder-gray-400 resize-none" />
                                    {errors.comment && <p className="text-red-500 text-xs ml-1">{errors.comment.message}</p>}
                                </div>

                                <div className="flex flex-col md:flex-row justify-between items-center pt-4 gap-6">
                                    {/* Recaptcha */}
                                    {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
                                        <div className="w-full md:w-auto flex flex-col items-center gap-2">
                                            <div className="bg-gray-50 px-6 py-3 rounded-xl border border-gray-200 flex items-center gap-4 shadow-sm w-full md:w-auto justify-center">
                                                <ReCAPTCHA
                                                    ref={recaptchaRef}
                                                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                                    onChange={handleCaptchaChange}
                                                />
                                            </div>
                                            {errors.recaptcha_token && <p className="text-red-500 text-xs">{errors.recaptcha_token.message}</p>}
                                        </div>
                                    )}

                                    <Button disabled={isSubmitting} type="submit" className="w-full md:w-auto rounded-full px-12 py-7 bg-red-600 text-white hover:bg-black transition-all duration-300 text-lg font-bold shadow-xl shadow-red-600/30 hover:shadow-black/20 hover:-translate-y-1">
                                        {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Submit Feedback"}
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Feedback;
